import { useCallback, useMemo, useRef } from "react";
import { useApi } from "@/contexts/api";
import { useMessageModal } from "@/contexts/layout/message-modal-context";
import { Temper } from "@/types/frontend-types";
import {
  ACCOUNT_SUSPENDED_ACCESS,
  ACCOUNT_SUSPENDED_BODY,
  ACCOUNT_SUSPENDED_TITLE,
  OUT_OF_CREDITS_ACKNOWLEDGE,
  OUT_OF_CREDITS_TITLE,
  TRASPASSING_ACKNOWLEDGE,
  TRASPASSING_BODY,
  TRASPASSING_TITLE,
  buildOutOfCreditsBody,
  hasActiveSubscriptionContract,
  isSuspendedSubscriptionContract,
} from "./session-data-provider-utils";

type SessionDataApi = ReturnType<typeof useApi>;
type ShowMessageModal = ReturnType<typeof useMessageModal>["showMessageModal"];

interface UseSessionAccessModalsInput {
  api: SessionDataApi;
  showMessageModal: ShowMessageModal;
}

interface SessionAccessModalActions {
  showInsufficientTokensModal: () => Promise<void>;
  showUnauthorizedSessionModal: () => void;
}

function showOutOfCreditsModal(input: {
  currentPeriodEnd: string | null;
  hasActiveContract: boolean;
  showMessageModal: ShowMessageModal;
}): void {
  input.showMessageModal({
    temper: Temper.Bad,
    title: OUT_OF_CREDITS_TITLE,
    body: buildOutOfCreditsBody({
      hasActiveContract: input.hasActiveContract,
      currentPeriodEnd: input.currentPeriodEnd,
    }),
    secondaryButtonText: OUT_OF_CREDITS_ACKNOWLEDGE,
  });
}

function showAccountSuspendedModal(input: {
  showMessageModal: ShowMessageModal;
}): void {
  input.showMessageModal({
    temper: Temper.Bad,
    title: ACCOUNT_SUSPENDED_TITLE,
    body: ACCOUNT_SUSPENDED_BODY,
    secondaryButtonText: OUT_OF_CREDITS_ACKNOWLEDGE,
    onSecondaryClick: () => {
      window.location.href = ACCOUNT_SUSPENDED_ACCESS.redirectTo;
    },
    closeOnSecondary: false,
    disableDismiss: ACCOUNT_SUSPENDED_ACCESS.disableDismiss,
  });
}

function useInsufficientTokensModalAction(input: {
  api: SessionDataApi;
  showMessageModal: ShowMessageModal;
}): () => Promise<void> {
  const { api, showMessageModal } = input;
  const insufficientTokensModalPendingRef = useRef(false);

  return useCallback(async (): Promise<void> => {
    if (insufficientTokensModalPendingRef.current) {
      return;
    }

    insufficientTokensModalPendingRef.current = true;

    try {
      const subscription = await api.subscription.current();
      if (isSuspendedSubscriptionContract({ subscription })) {
        showAccountSuspendedModal({ showMessageModal });
        return;
      }
      showOutOfCreditsModal({
        currentPeriodEnd: subscription.contract.currentPeriodEnd,
        hasActiveContract: hasActiveSubscriptionContract({ subscription }),
        showMessageModal,
      });
    } catch {
      showOutOfCreditsModal({
        currentPeriodEnd: null,
        hasActiveContract: false,
        showMessageModal,
      });
    } finally {
      insufficientTokensModalPendingRef.current = false;
    }
  }, [api, showMessageModal]);
}

function useUnauthorizedSessionModalAction(input: {
  showMessageModal: ShowMessageModal;
}): () => void {
  const { showMessageModal } = input;

  return useCallback((): void => {
    showMessageModal({
      temper: Temper.Bad,
      title: TRASPASSING_TITLE,
      hackerAllCaps: true,
      body: TRASPASSING_BODY,
      secondaryButtonText: TRASPASSING_ACKNOWLEDGE,
      onSecondaryClick: () => {
        window.location.href = "/";
      },
      closeOnSecondary: false,
      disableDismiss: true,
    });
  }, [showMessageModal]);
}

export function useSessionAccessModals(
  input: UseSessionAccessModalsInput,
): SessionAccessModalActions {
  const { api, showMessageModal } = input;
  const showInsufficientTokensModal = useInsufficientTokensModalAction({
    api,
    showMessageModal,
  });
  const showUnauthorizedSessionModal = useUnauthorizedSessionModalAction({
    showMessageModal,
  });

  return useMemo(
    () => ({
      showInsufficientTokensModal,
      showUnauthorizedSessionModal,
    }),
    [showInsufficientTokensModal, showUnauthorizedSessionModal],
  );
}
